import {
    FormControl,
    styled,
    Button,
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
} from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"
import { t } from "@lingui/macro"
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank"
import CheckBoxIcon from "@mui/icons-material/CheckBox"

import { FeaturesAdminService } from "src/api/services/FeaturesAdminService"
import { ListPage } from "src/components/ListPage"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { FeatureTogglesStore } from "src/views/feature-toggles/store"
import { FeatureTogglesTableView } from "src/views/feature-toggles/feature-toggles-table"
import {
    reportError,
    reportSuccess,
    reportUnhandledApiError,
} from "src/lib/report"
import { GlobalStore } from "src/store"
import { ISessionLegalEntity } from "src/store/session"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const View = observer(() => {
    const store = useStore(FeatureTogglesStore)
    const header = useMemo(
        () => ({
            header: t`feature-toggles-view.header`,
            breadcrumbs: [],
        }),
        [],
    )
    const globalStore = useStore(GlobalStore)

    // Transform legal entities to AutoCompleteTextField format
    const legalEntityOptions = useMemo(() => {
        return store.legalEntities.map((legalEntity) => ({
            id: legalEntity.legalEntityId ?? "",
            label: legalEntity.legalName ?? "",
        }))
    }, [store.legalEntities])

    // Transform selected legal entities to AutoCompleteTextField format
    const selectedLegalEntityOptions = useMemo(() => {
        return store.selectedLegalEntities.map((legalEntity) => ({
            id: legalEntity.legalEntityId ?? "",
            label: legalEntity.legalName ?? "",
        }))
    }, [store.selectedLegalEntities])

    // Convert property owners to AutoCompleteTextField format
    const propertyOwnerOptions = useMemo(() => {
        return globalStore.session.propertyOwners
            .filter(
                (propertyOwner) =>
                    propertyOwner.propertyOwnerId != null &&
                    propertyOwner.legalName != null,
            )
            .map((propertyOwner) => ({
                id: propertyOwner.propertyOwnerId ?? "",
                label: propertyOwner.legalName ?? "",
            }))
    }, [globalStore.session.propertyOwners])

    // Get selected property owner as an option
    const selectedPropertyOwnerOption = useMemo(() => {
        if (store.selectedPropertyOwnerId === undefined) return null

        return (
            propertyOwnerOptions.find(
                (option) =>
                    option.id.toString() === store.selectedPropertyOwnerId,
            ) ?? null
        )
    }, [propertyOwnerOptions, store.selectedPropertyOwnerId])

    const handlePropertyOwnerChange = useCallback(
        async (_, value) => {
            try {
                store.setIsLoading(true)

                // If value is null (user cleared the selection)
                if (value === null) {
                    // Reset property owner selection
                    store.setSelectedPropertyOwnerId("")
                    store.setSelectedLegalEntities([])
                    store.setLegalEntities([])
                    store.setTableData([])
                    store.setIsLoading(false)
                    return
                }

                const selectedPropertyOwnerId = value.id
                store.setSelectedPropertyOwnerId(
                    String(selectedPropertyOwnerId),
                )

                // Reset selected legal entities when property owner changes
                store.setSelectedLegalEntities([])

                const legalEntities = globalStore.session.propertyOwners.filter(
                    (propertyOwner) =>
                        propertyOwner.propertyOwnerId ===
                        selectedPropertyOwnerId,
                )[0].legalEntities
                store.setLegalEntities(legalEntities ?? [])
                await store.populateTableData()
                if (store.legalEntities.length === 0) {
                    reportError(t`feature-toggles-view.no-legal-entities-found`)
                }
            } catch (e) {
                reportUnhandledApiError(e)
            } finally {
                store.setIsLoading(false)
            }
        },
        [store, globalStore.session.propertyOwners],
    )

    const handleSelectLegalEntities = useCallback(
        async (_, value) => {
            try {
                store.setIsLoading(true)

                // Handle all possible value types
                let selections = Array.isArray(value)
                    ? value
                    : value !== null && typeof value === "object"
                    ? [value]
                    : []

                const originalLegalEntities = selections
                    .map((option) => {
                        const id =
                            typeof option === "object" ? option.id : option
                        return store.legalEntities.find(
                            (entity) =>
                                entity.legalEntityId?.toString() ===
                                id?.toString(),
                        )
                    })
                    .filter(Boolean) as ISessionLegalEntity[]

                store.setSelectedLegalEntities(originalLegalEntities)
                await store.populateTableData()
            } catch (e) {
                reportUnhandledApiError(e)
            } finally {
                store.setIsLoading(false)
            }
        },
        [store],
    )

    const handleSelectAllLegalEntities = useCallback(async () => {
        try {
            store.setIsLoading(true)
            store.setSelectedLegalEntities(store.legalEntities)
            await store.populateTableData()
        } catch (e) {
            reportUnhandledApiError(e)
        } finally {
            store.setIsLoading(false)
        }
    }, [store])

    const handleSelectAllLegalEntitiyIdsWithoutFeatures =
        useCallback(async () => {
            try {
                store.setIsLoading(true)
                const filterOutLegalEntetiesId =
                    await FeaturesAdminService.getV1AdminFeaturesFlagHasFlags({
                        propertyOwnerId: Number(store.selectedPropertyOwnerId),
                    })
                store.setSelectedLegalEntities(
                    store.legalEntities?.filter(
                        (legalEntity) =>
                            !filterOutLegalEntetiesId.includes(
                                Number(legalEntity.legalEntityId),
                            ),
                    ),
                )
                await store.populateTableData()
            } catch (e) {
                reportUnhandledApiError(e)
            } finally {
                store.setIsLoading(false)
            }
        }, [store])

    const handleSelectNoLegalEntities = useCallback(async () => {
        try {
            store.setIsLoading(true)
            store.setSelectedLegalEntities([])
            await store.populateTableData()
        } catch (e) {
            reportUnhandledApiError(e)
        } finally {
            store.setIsLoading(false)
        }
    }, [store])

    const handleSubmit = useCallback(async () => {
        try {
            store.setIsLoading(true)
            store.populateModifiedFeatureFlags()
            if (store.modifiedFeatureFlags.length > 0) {
                await FeaturesAdminService.putV1AdminFeaturesFlag({
                    request: {
                        feature_flags: store.modifiedFeatureFlags,
                    },
                })
                reportSuccess(t`global.saved-changes`)
                await store.populateTableData()
            }
        } catch {
            reportError(t`errors.request-failed-with-server-error-alert`)
            store.setTableData([])
        } finally {
            store.setIsLoading(false)
        }
    }, [store])

    const handleUndoChanges = useCallback(async () => {
        try {
            store.setIsLoading(true)
            await store.populateTableData()
        } catch (e) {
            reportUnhandledApiError(e)
        } finally {
            store.setIsLoading(false)
        }
    }, [store])

    const renderOptionItem = useCallback(
        (props, option, { selected }) => (
            <li key={option.id} {...props}>
                <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                />
                {option.label}
            </li>
        ),
        [],
    )

    return (
        <ListPage header={header}>
            {globalStore.session.propertyOwners.length > 0 && (
                <>
                    <StyledBox
                        sx={{
                            gridAutoColumns: {
                                md: "minmax(min-content, max-content)",
                            },
                            gridAutoFlow: {
                                xs: "row",
                                md: "column",
                            },
                        }}
                    >
                        <FormControl fullWidth>
                            <AutoCompleteTextField
                                label={t`feature-toggles-view.property-owner`}
                                options={propertyOwnerOptions}
                                value={selectedPropertyOwnerOption}
                                onChange={handlePropertyOwnerChange}
                                sx={{
                                    minWidth: 250,
                                }}
                            />
                        </FormControl>
                    </StyledBox>

                    {store.legalEntities.length > 0 && (
                        <StyledPaper
                            sx={{
                                flexDirection: {
                                    xs: "column",
                                    sm: "column",
                                    md: "row",
                                    justifyContent: "flex-end",
                                },
                                width: "auto",
                            }}
                        >
                            <AutoCompleteTextField
                                multiple
                                includeInputInList
                                limitTags={2}
                                id="legal-entities"
                                disableCloseOnSelect
                                options={legalEntityOptions}
                                value={selectedLegalEntityOptions}
                                onChange={handleSelectLegalEntities}
                                label={t`feature-toggles-view.legal-entities`}
                                renderOption={renderOptionItem}
                                sx={{
                                    width: "100%",
                                    maxWidth: {
                                        xs: "100%",
                                        sm: "100%",
                                        md: 300,
                                    },
                                }}
                            />

                            <FormControlLabel
                                htmlFor="select-all-legal-entities"
                                label={t`global.select-all`}
                                control={
                                    <Checkbox
                                        id="select-all-legal-entities"
                                        checked={
                                            store.selectedLegalEntities
                                                .length ===
                                            store.legalEntities.length
                                        }
                                        indeterminate={
                                            store.selectedLegalEntities.length >
                                                0 &&
                                            store.selectedLegalEntities
                                                .length !==
                                                store.legalEntities.length
                                        }
                                        onChange={(event) =>
                                            event.target.checked
                                                ? handleSelectAllLegalEntities()
                                                : handleSelectNoLegalEntities()
                                        }
                                    />
                                }
                                sx={{ flexShrink: 0 }}
                            />

                            <Button
                                size="small"
                                color="info"
                                variant="contained"
                                onClick={
                                    handleSelectAllLegalEntitiyIdsWithoutFeatures
                                }
                                type="button"
                                sx={{ flexShrink: 0 }}
                                data-testid="select-all-without-features-button"
                            >
                                {t`feature-toggles-view.select-all-without-features`}
                            </Button>
                        </StyledPaper>
                    )}
                </>
            )}

            {store.tableData.length > 1 && (
                <>
                    <FeatureTogglesTableView />
                    <StyledBox
                        sx={{
                            gridAutoColumns: {
                                md: "minmax(min-content, max-content)",
                            },
                            gridAutoFlow: {
                                xs: "row",
                                md: "column",
                            },
                        }}
                    >
                        <Button
                            color="info"
                            variant="contained"
                            type="button"
                            onClick={handleSubmit}
                            disabled={!store.dirtyFeatureFlags}
                            data-testid="save-button"
                        >
                            {t`global.save`}
                        </Button>
                        <Button
                            color="info"
                            variant="text"
                            onClick={handleUndoChanges}
                            disabled={!store.dirtyFeatureFlags}
                            type="button"
                            data-testid="undo-changes-button"
                        >
                            {t`global.undo-changes`}
                        </Button>
                    </StyledBox>
                </>
            )}
        </ListPage>
    )
})

export const FeatureTogglesView = () => (
    <StoreProvider Store={FeatureTogglesStore}>
        <View />
    </StoreProvider>
)

const StyledPaper = styled(Paper)(() => ({
    borderWidth: "0",
    padding: "20px",
    gap: "20px",
    alignItems: "center",
    display: "flex",
}))

const StyledBox = styled(Box)(() => ({
    display: "grid",
    gap: "20px",
    alignItems: "center",
}))
